// The read/write posture model (Brief 5 / pt7 + pt9) — the HONEST truth about
// what Waytide is doing to a watched session, derived from observable facts, not
// intentions. Pure + side-effect-free so the whole honesty contract is
// unit-tested without React.
//
// CANON (pt9): Waytide is READ-ONLY ~99% of the time. The ONLY genuine WRITE into
// a session is (a) the initial HOOK INJECTION — the bridge attaching itself — and
// (b) a STEER that reaches the running agent (a pause/resume control token, or a
// delivered message). Reading the agent's output, the trace, the operators'
// asides (Signal Q&A, never delivered to the agent), the wiki — all READ.
//
// Corrections baked in:
//   * TRUTH: never flash "writing" on the optimistic click. A send /
//     stop / resume is QUEUED; the write is only real once it has LANDED. So a
//     write event is derived from a CONFIRMED fact — the bridge attaching, or the
//     session's TRUE status transitioning (the steer took effect over presence) —
//     never from the user's button press. And we never CLAIM read-only while a
//     steer is in flight or the session isn't confirmed-attached: that is
//     `unknown`, not `read-only`.
//   * CALM: a write must leave a felt trace, not a vanishing flash — so
//     the posture carries a running COUNT of writes seen this session (the 99%
//     ratio felt as a number), and the momentary write NAMES what + where.
//
// This module is the single source of "is it a write, and what do we say"; the
// indicator component renders it, the session hook feeds it observed facts.

/** What the posture is right now. `read-only` is the resting truth (the 99%);
 * `write` is the momentary, named genuine write; `unknown` is the honest
 * "we can't claim read-only" state (the session isn't confirmed-attached, or a
 * steer is mid-flight) — never dressed up as calm read-only. A string union (not
 * an enum) so it erases at build and reads plainly. */
export type Posture = "read-only" | "write" | "unknown";

/** The kind of a genuine write — the two things that actually write into a
 * session. `connect` = the hook injection (the bridge attaching itself);
 * `steer` = a control token / delivered steer that reached the agent. Used to
 * pick the named copy; both are equally "a write". */
export type WriteKind = "connect" | "steer";

/** One observed genuine write — a confirmed fact, with what + where, and when we
 * saw it. `where` is a repo-relative location to point at on the schematic (the
 * bound repo, or an area within it) — "" when we only know it's a write, not its
 * spot. `at` is epoch-ms, so the indicator can recede the momentary state after
 * a calm interval and the count can be ordered. */
export interface WriteEvent {
  kind: WriteKind;
  /** A short, plain noun phrase: what we wrote. e.g. "connecting bridge",
   * "steer delivered", "paused the agent". */
  what: string;
  /** Where it landed — a repo full_name or repo-relative area path, or "". */
  where: string;
  /** Epoch-ms the write was observed (a confirmed transition, never a click). */
  at: number;
}

/** The inputs the posture is derived from — all OBSERVABLE, confirmed facts the
 * session surface already has. No intentions, no optimistic echoes. */
export interface PostureInput {
  /** Is the session confirmed ATTACHED right now (the bridge is live / presence
   * proves a real channel)? When false we cannot claim read-only OR assert a
   * write honestly — the posture is `unknown`. */
  attached: boolean;
  /** The most recent genuine write observed this session, or null. Drives the
   * momentary `write` state while it's still "fresh" (see `WRITE_HOLD_MS`). */
  lastWrite: WriteEvent | null;
  /** How many genuine writes have been observed this session — the felt ratio.
   * Shown as "read-only · N writes" so the 99% is a number, not a
   * vanishing flash. */
  writeCount: number;
  /** Now, epoch-ms (injectable for deterministic tests). */
  now: number;
}

/** How long the momentary `write` state stays up after a write lands, before it
 * recedes back to read-only. A few seconds: long enough to read "steer
 * delivered · repo", short enough to stay calm and not linger as alarm. */
export const WRITE_HOLD_MS = 4000;

/** The resolved posture the indicator renders: the state, the felt count, and
 * (when `write`) the named event. Pure value — no React, no DOM. */
export interface ResolvedPosture {
  posture: Posture;
  /** The genuine writes seen this session (the felt ratio). */
  writeCount: number;
  /** The active write event while `posture === "write"`, else null. */
  active: WriteEvent | null;
  /** The plain resting label: "read-only" (or "watching" when unknown — we don't
   * claim read-only we can't back). The component lowercases via CSS-free copy. */
  restLabel: string;
}

/** Resolve the honest posture from observed facts (pure). The order encodes the
 * honesty rules:
 *   1. Not attached ⇒ `unknown` — we can prove neither reads nor writes, so we
 *      do NOT claim read-only. Resting label is "watching", not a
 *      false "read-only".
 *   2. A fresh confirmed write ⇒ `write`, naming what + where, until it recedes.
 *   3. Otherwise ⇒ `read-only` — the resting truth, with the felt write count.
 */
export function resolvePosture(input: PostureInput): ResolvedPosture {
  const { attached, lastWrite, writeCount, now } = input;

  if (!attached) {
    return {
      posture: "unknown",
      writeCount,
      active: null,
      // Honest: we're connected to the surface but can't assert the read-only
      // channel — say what we ARE doing (watching), never a read-only we can't back.
      restLabel: "watching",
    };
  }

  const writeIsFresh =
    lastWrite != null && now - lastWrite.at >= 0 && now - lastWrite.at < WRITE_HOLD_MS;

  if (writeIsFresh && lastWrite != null) {
    return {
      posture: "write",
      writeCount,
      active: lastWrite,
      restLabel: "read-only",
    };
  }

  return {
    posture: "read-only",
    writeCount,
    active: null,
    restLabel: "read-only",
  };
}

/** Build the named `WriteEvent` for the HOOK INJECTION — the bridge attaching
 * itself into a session, the first genuine write (pt9). `where` is the bound
 * repo's full_name when known. Pure constructor so the copy lives in one place. */
export function connectWrite(repoFullName: string, at: number): WriteEvent {
  return {
    kind: "connect",
    what: "connecting bridge",
    where: repoFullName,
    at,
  };
}

/** Build the named `WriteEvent` for a STEER that took effect — a confirmed
 * status transition (the agent was paused/resumed) or a delivered control token.
 * `verb` is the plain past-tense fact ("paused the agent", "resumed the agent",
 * "steer delivered"); `where` is the bound repo / area. Pure. */
export function steerWrite(verb: string, where: string, at: number): WriteEvent {
  return {
    kind: "steer",
    what: verb,
    where,
    at,
  };
}

/** Map a session's TRUE status transition (the value that LANDED over presence,
 * not an optimistic echo) to the plain steer verb, or null when the transition
 * is not a write into the agent. Only the transitions a steer CAUSES count:
 *   running/idle → paused|halted  ⇒ "paused the agent" (a stop landed)
 *   paused|halted → running|idle  ⇒ "resumed the agent" (a resume landed)
 * A transition INTO needs_input/done is the agent's own move, not our write, so
 * it returns null (we wrote nothing). Pure — unit-tested across the matrix. */
export function steerVerbForTransition(
  prevStatus: string,
  nextStatus: string,
): string | null {
  if (prevStatus === nextStatus) return null;
  const wasStopped = prevStatus === "paused" || prevStatus === "halted";
  const isStopped = nextStatus === "paused" || nextStatus === "halted";
  const wasRunning = prevStatus === "running" || prevStatus === "idle";
  const isRunning = nextStatus === "running" || nextStatus === "idle";

  if (wasRunning && isStopped) return "paused the agent";
  if (wasStopped && isRunning) return "resumed the agent";
  // Any other transition (→ needs_input, → done, first-seen "") is not our write.
  return null;
}

/** Fold an op's repo-relative path to the nearest SHOWN area path by
 * longest-prefix match (the indicator contract), so "a write landed HERE"
 * points at a real tile on the schematic rather than a path that isn't drawn.
 * `areaPaths` are the area ids the map rendered; returns the longest one that is
 * a path-prefix of `opPath`, or "" when none contains it. Pure.
 *
 * Matching is segment-aware: `src/api` matches `src/api/users.rs` and `src/api`
 * itself, but NOT `src/apixx` (a prefix string but a different directory). */
export function foldToArea(opPath: string, areaPaths: string[]): string {
  let best = "";
  for (const area of areaPaths) {
    if (opPath !== area && !opPath.startsWith(area + "/")) continue;
    if (area.length > best.length) best = area;
  }
  return best;
}
